import { Fragment, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, till } from "@/lib/api";
import { dateTime, fmtSerial, money, personName, time, toMinor } from "@/lib/format";
import { beep, useScanner } from "@/lib/scanner";
import type { Item, Move } from "@/lib/types";
import { CameraScanner } from "@/components/CameraScanner";
import { Badge, Button, Field, Input, Modal, Pager, Select, cx, usePaged, useToast } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";
import { useShop, useTill } from "@/lib/till-context";
import { PlainReceive } from "@/components/PlainReceive";

type Line = { itemId: string; qty: string; cost: string; serials: string[] };
const KIND_TONE: Record<string, "slate" | "brand" | "blue" | "amber" | "red" | "violet"> = { sale: "slate", return: "brand", receive: "blue", transfer_in: "amber", transfer_out: "amber", rma: "red", writeoff: "red", stocktake: "violet", adjust: "violet" };
const KIND_LABEL: Record<string, string> = { sale: "Sale", return: "Return", receive: "Received", transfer_in: "From website", transfer_out: "To website", rma: "To supplier", writeoff: "Write-off", stocktake: "Stock count", adjust: "Correction" };
/** Buildwise counts deliveries in by hand: no scanning and no serial numbers on this page. */
const PLAIN = !!import.meta.env.VITE_BRAND;
const LOG_PAGE = 15;

/** Buildwise: Receive, Count and the Stock log are separate pages. */
export function StockMoves() {
  const [sp] = useSearchParams();
  if (!PLAIN) return <LegacyStock />;
  if (sp.get("tab") === "count") return <Navigate to="/count" replace />;
  return <PlainReceivePage />;
}

function PlainReceivePage() {
  const [sp] = useSearchParams();
  const items = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")) });
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: { id: string; name: string }[] }>(till("/admin/shops")) });
  if (!shopsQ.data || !items.data) return <p className="text-sm text-slate-500">Loading…</p>;
  return <PlainReceive items={items.data.items} shops={shopsQ.data.shops} preselect={sp.get("item")} preselectMany={(sp.get("items") ?? "").split(",").filter(Boolean)} onDone={() => { void items.refetch(); }} />;
}

export function StockLogPage() {
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: { id: string; name: string }[] }>(till("/admin/shops")) });
  const shops = shopsQ.data?.shops ?? [];
  const [shopId, setShopId] = useState("");
  return (
    <>
      <PageHead title="Stock log" sub="Every change to stock, newest first.">
        {shops.length > 1 ? <Select aria-label="Shop" value={shopId} onChange={(e) => setShopId(e.target.value)} className="!w-48"><option value="">All shops</option>{shops.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select> : null}
      </PageHead>
      <Log shopId={shopId} />
    </>
  );
}

function LegacyStock() {
  const [sp, setSp] = useSearchParams();
  const shop = useShop();
  const tab = sp.get("tab") === "count" ? "count" : sp.get("tab") === "transfer" && shop.website ? "transfer" : "receive";
  const items = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")) });
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: { id: string; name: string }[] }>(till("/admin/shops")) });
  const shops = shopsQ.data?.shops ?? [];
  const [shopId, setShopId] = useState("");
  const shopItems = (items.data?.items ?? []).filter((i) => !shopId || i.location === shopId);
  return (
    <>
      <PageHead title={shop.website ? "Receive & transfer" : "Receive & count stock"} sub={PLAIN ? "Add deliveries to shop stock. Every change to stock is logged below." : `${shop.serials ? "Scan deliveries in unit by unit" : "Count deliveries in"} · every change to shop stock is logged`}>
        {shops.length ? (
          <Select aria-label="Shop" value={shopId} onChange={(e) => setShopId(e.target.value)} className="!w-48">
            <option value="">All shops</option>
            {shops.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </Select>
        ) : null}
        <Button tone={tab === "receive" ? "dark" : "light"} onClick={() => setSp({})}>
          Receive a delivery
        </Button>
        <Button tone={tab === "count" ? "dark" : "light"} onClick={() => setSp({ tab: "count" })}>
          Count stock
        </Button>
        {shop.website ? (
          <Button tone={tab === "transfer" ? "dark" : "light"} onClick={() => setSp({ tab: "transfer" })}>
            Transfer shop ↔ website
          </Button>
        ) : null}
      </PageHead>
      <div className="flex flex-col gap-4">
        {tab === "count" ? <CountStock items={shopItems} shopId={shopId} /> : tab === "receive" ? <Receive items={shopItems} preselect={sp.get("item")} /> : <Transfer items={items.data?.items ?? []} preselect={sp.get("item")} />}
        {shop.serials && !PLAIN ? <FindUnit /> : null}
        <Log shopId={shopId} />
      </div>
    </>
  );
}

function Receive({ items, preselect }: { items: Item[]; preselect: string | null }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [supplier, setSupplier] = useState("");
  const [ref, setRef] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [active, setActive] = useState<number>(-1);
  const [scan, setScan] = useState("");
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string>("");
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  useEffect(() => {
    if (preselect && byId.get(preselect) && !lines.length) {
      setLines([{ itemId: preselect, qty: "", cost: "", serials: [] }]);
      setActive(0);
    }
  }, [preselect, byId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** A scan is either a product barcode (start/select that line) or a unit's IMEI for the selected device line. */
  const onScan = (raw: string) => {
    const code = raw.trim();
    setScan("");
    const item = items.find((i) => i.barcode === code || (i.sku && i.sku === code));
    if (item) {
      const idx = lines.findIndex((l) => l.itemId === item.id);
      if (idx >= 0) {
        setActive(idx);
        if (item.tracking === "qty") setLines(lines.map((l, i) => (i === idx ? { ...l, qty: String((Number(l.qty) || 0) + 1) } : l)));
      } else {
        setLines([...lines, { itemId: item.id, qty: item.tracking === "qty" ? "1" : "", cost: "", serials: [] }]);
        setActive(lines.length);
      }
      beep(true);
      setNote(item.tracking === "serial" ? `${item.name}: now scan each unit's IMEI / serial` : `${item.name} +1`);
      return;
    }
    const cur = lines[active];
    const curItem = cur ? byId.get(cur.itemId) : null;
    if (cur && curItem?.tracking === "serial") {
      const sn = code.replace(/[\s-]+/g, "").toUpperCase();
      if (cur.serials.includes(sn) || lines.some((l) => l.serials.includes(sn))) {
        beep(false);
        setNote(`${fmtSerial(sn)} is already scanned`);
        return;
      }
      setLines(lines.map((l, i) => (i === active ? { ...l, serials: [...l.serials, sn] } : l)));
      beep(true);
      setNote(`${curItem.name} · ${fmtSerial(sn)} ✓`);
      return;
    }
    beep(false);
    setNote(`${code} isn't a shop product's barcode. Pick the product below first, or add the barcode to it in Products`);
  };
  const noScan = useTill().me?.settings.scanning === false || PLAIN;
  useScanner(onScan, !noScan && !camera);

  const units = lines.reduce((n, l) => n + (byId.get(l.itemId)?.tracking === "serial" ? l.serials.length : Number(l.qty) || 0), 0);
  const cost = lines.reduce((n, l) => n + toMinor(l.cost) * (byId.get(l.itemId)?.tracking === "serial" ? l.serials.length : Number(l.qty) || 0), 0);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api<{ units: number; dupes: string[] }>(till("/admin/receive"), { body: { supplier, ref, lines: lines.filter((l) => l.serials.length || Number(l.qty) > 0).map((l) => ({ itemId: l.itemId, qty: Number(l.qty) || 0, cost: l.cost ? toMinor(l.cost) : null, serials: l.serials })) } });
      toast(`${r.units} unit${r.units === 1 ? "" : "s"} added to shop stock${r.dupes.length ? ` · ${r.dupes.length} duplicate serial(s) skipped` : ""}`);
      setLines([]);
      setSupplier("");
      setRef("");
      setActive(-1);
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      void qc.invalidateQueries({ queryKey: ["moves"] });
      void qc.invalidateQueries({ queryKey: ["catalog"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't receive", "err");
    } finally {
      setBusy(false);
    }
  };
  const addItem = (id: string) => {
    const at = lines.findIndex((l) => l.itemId === id);
    if (at >= 0) return setActive(at);
    setLines([...lines, { itemId: id, qty: "", cost: "", serials: [] }]);
    setActive(lines.length);
  };
  const setLine = (i: number, patch: Partial<Line>) => setLines(lines.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const row = "grid grid-cols-2 items-center gap-2.5 md:grid-cols-[minmax(0,1fr)_110px_130px_110px_44px]";
  return (
    <Card title="Receive a delivery">
      <div className="flex flex-col gap-4 px-5 pb-5 pt-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Supplier">
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Who delivered it" />
          </Field>
          <Field label="Invoice or waybill number (optional)">
            <Input value={ref} onChange={(e) => setRef(e.target.value)} />
          </Field>
        </div>
        {noScan ? null : <div className="flex gap-2">
          <form onSubmit={(e) => { e.preventDefault(); if (scan) onScan(scan); }} className="flex h-12 min-w-0 flex-1 items-center gap-2 border-2 border-brand px-3">
            <input data-scan-target value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan the box barcode, then each unit's IMEI / serial" className="min-w-0 flex-1 font-mono text-[15px] font-bold outline-none" />
          </form>
          <Button onClick={() => setCamera(true)} className="!h-12">
            Camera
          </Button>
        </div>}
        {note ? <p className="bg-slate-100 px-3 py-2 text-[13px] font-semibold">{note}</p> : null}
        <ProductPick items={items.filter((i) => i.active && i.tracking !== "service" && !lines.some((l) => l.itemId === i.id))} onPick={addItem} />
        {lines.length ? (
          <div className="flex flex-col gap-2">
            <div className={cx(row, "hidden px-3 text-xs font-semibold text-slate-500 md:grid")}><span>Product</span><span>Quantity</span><span>Cost each</span><span className="text-right">Line cost</span><span /></div>
            {lines.map((l, i) => {
              const it = byId.get(l.itemId);
              if (!it) return null;
              const serial = it.tracking === "serial";
              const n = serial ? l.serials.length : Number(l.qty) || 0;
              return (
                <div key={l.itemId} onClick={() => setActive(i)} className={cx("rounded-xl px-3 py-2", active === i && serial ? "bg-brand-tint" : "bg-slate-50")}>
                  <div className={row}>
                    <span className="col-span-2 min-w-0 md:col-span-1">
                      <b className="block text-sm font-semibold">{it.name}</b>
                      <span className="text-xs text-slate-500">{(it.onHand ?? 0).toLocaleString("en-GB")} in stock now{n ? `, ${((it.onHand ?? 0) + n).toLocaleString("en-GB")} after this` : ""}</span>
                    </span>
                    {serial ? <b className="font-mono">{l.serials.length}</b> : <Input aria-label={`Quantity of ${it.name}`} inputMode="numeric" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value.replace(/\D/g, "") })} placeholder="0" className="!h-11 font-mono" />}
                    <Input aria-label={`Cost each of ${it.name}`} inputMode="decimal" value={l.cost} onChange={(e) => setLine(i, { cost: e.target.value })} placeholder="0.00" className="!h-11 font-mono" />
                    <b className="text-right font-mono text-sm">{money(toMinor(l.cost) * n)}</b>
                    <button type="button" aria-label={`Remove ${it.name}`} onClick={(e) => { e.stopPropagation(); setLines(lines.filter((_, j) => j !== i)); setActive(-1); }} className="h-11 w-11 justify-self-end text-xl text-slate-500 hover:text-red-700">×</button>
                  </div>
                  {serial ? (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {l.serials.map((sn) => (
                        <button key={sn} type="button" title="Remove" onClick={(e) => { e.stopPropagation(); setLine(i, { serials: l.serials.filter((s) => s !== sn) }); }} className="bg-white px-1.5 py-0.5 font-mono text-[11px] ring-1 ring-slate-200">
                          {fmtSerial(sn)} ×
                        </button>
                      ))}
                      {!l.serials.length ? <span className="text-xs text-amber-800">{active === i ? "Scan each unit now" : "Tap to select, then scan units"}</span> : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">Search for a product above to start this delivery.</p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3.5">
          {units ? <span className="text-sm text-slate-500">{units.toLocaleString("en-GB")} unit{units === 1 ? "" : "s"} · cost {money(cost)}</span> : null}
          <Button tone="primary" onClick={submit} disabled={busy || !units}>
            {busy ? "Saving…" : units ? `Add ${units.toLocaleString("en-GB")} to shop stock` : "Add to shop stock"}
          </Button>
        </div>
      </div>
      {camera ? <CameraScanner title="Scan barcodes and IMEIs" onCode={onScan} onClose={() => setCamera(false)}><p className="p-4 text-sm">{note}</p></CameraScanner> : null}
    </Card>
  );
}

/** Type part of a name and pick from the matches. */
function ProductPick({ items, onPick }: { items: Item[]; onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hits = items.filter((i) => words.every((w) => i.name.toLowerCase().includes(w))).slice(0, 8);
  const pick = (id: string) => {
    onPick(id);
    setQ("");
    setOpen(false);
  };
  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
      <Field label="Add a product">
        <Input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); if (e.key === "Enter" && hits[0]) { e.preventDefault(); pick(hits[0].id); } }} placeholder="Type a product name" autoComplete="off" />
      </Field>
      {open ? (
        <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl bg-white p-1 shadow-[0_12px_30px_rgba(11,21,23,0.18)] ring-1 ring-slate-200">
          {hits.map((i) => (
            <button key={i.id} type="button" onClick={() => pick(i.id)} className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-slate-50">
              <span className="min-w-0 font-semibold">{i.name}</span>
              <span className="flex-none text-xs text-slate-500">{(i.onHand ?? 0).toLocaleString("en-GB")} in stock</span>
            </button>
          ))}
          {!hits.length ? <p className="px-3 py-3 text-sm text-slate-500">No product matches.</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function Transfer({ items, preselect }: { items: Item[]; preselect: string | null }) {
  const toast = useToast();
  const qc = useQueryClient();
  const linked = items.filter((i) => i.webSlug && i.tracking !== "service");
  const [itemId, setItemId] = useState(preselect ?? "");
  const [dir, setDir] = useState<"to_shop" | "to_web">("to_shop");
  const [qty, setQty] = useState("");
  const [serials, setSerials] = useState<string[]>([]);
  const [scan, setScan] = useState("");
  const it = linked.find((i) => i.id === itemId);
  const n = it?.tracking === "serial" ? serials.length : Number(qty) || 0;
  useScanner((c) => {
    const sn = c.replace(/[\s-]+/g, "").toUpperCase();
    if (it?.tracking === "serial" && !serials.includes(sn)) setSerials([...serials, sn]);
  });
  const submit = async () => {
    try {
      const r = await api<{ qty: number; ref: string }>(till("/admin/transfer"), { body: { itemId, direction: dir, qty: Number(qty) || 0, serials } });
      toast(`Moved ${r.qty} ${dir === "to_shop" ? "from the website to the shop" : "from the shop to the website"} · ${r.ref}`);
      setQty("");
      setSerials([]);
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      void qc.invalidateQueries({ queryKey: ["moves"] });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Transfer failed", "err");
    }
  };
  return (
    <Card title="Transfer between shop & website">
      <div className="grid gap-4 px-5 pb-5 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 border border-slate-200">
            <button type="button" onClick={() => setDir("to_shop")} className={cx("h-11 text-sm font-semibold", dir === "to_shop" ? "bg-slate-900 text-white" : "bg-white")}>
              Website → Shop
            </button>
            <button type="button" onClick={() => setDir("to_web")} className={cx("h-11 text-sm font-semibold", dir === "to_web" ? "bg-slate-900 text-white" : "bg-white")}>
              Shop → Website
            </button>
          </div>
          <Field label="Product (linked to the website)">
            <Select value={itemId} onChange={(e) => { setItemId(e.target.value); setSerials([]); }}>
              <option value="">Choose…</option>
              {linked.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </Select>
          </Field>
          {it?.tracking === "serial" ? (
            <Field label="Scan the units being moved" hint={dir === "to_shop" ? "Each unit's IMEI / serial is added to shop stock" : "Units must be in shop stock"}>
              <form onSubmit={(e) => { e.preventDefault(); const sn = scan.replace(/[\s-]+/g, "").toUpperCase(); if (sn && !serials.includes(sn)) setSerials([...serials, sn]); setScan(""); }}>
                <Input data-scan-target value={scan} onChange={(e) => setScan(e.target.value)} placeholder="IMEI / serial" className="font-mono" />
              </form>
              <div className="flex flex-wrap gap-1">
                {serials.map((s) => (
                  <button key={s} type="button" onClick={() => setSerials(serials.filter((x) => x !== s))} className="bg-slate-100 px-1.5 py-0.5 font-mono text-[11px]">
                    {fmtSerial(s)} ×
                  </button>
                ))}
              </div>
            </Field>
          ) : it ? (
            <Field label="Units">
              <Input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg" />
            </Field>
          ) : null}
        </div>
        {it ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 p-3">
                <span className="text-xs text-slate-500">Website</span>
                <div className="font-mono text-xl font-bold">{it.webStock == null ? "∞" : `${it.webStock} → ${dir === "to_shop" ? it.webStock - n : it.webStock + n}`}</div>
              </div>
              <div className="bg-brand-tint p-3">
                <span className="text-xs text-brand-ink">Shop</span>
                <div className="font-mono text-xl font-bold">
                  {it.onHand ?? 0} → {dir === "to_shop" ? (it.onHand ?? 0) + n : (it.onHand ?? 0) - n}
                </div>
              </div>
            </div>
            <Button tone="primary" onClick={submit} disabled={!n}>
              Transfer {n} unit{n === 1 ? "" : "s"}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{linked.length ? "Choose a product." : "No products are linked to the website yet. Import them in Products."}</p>
        )}
      </div>
    </Card>
  );
}

function FindUnit() {
  const [sn, setSn] = useState("");
  const [go, setGo] = useState("");
  const q = useQuery({ queryKey: ["unit", go], queryFn: () => api<{ unit: { serial: string; status: string; receivedRef: string | null }; item: { name: string; price: number } | null; trail: Move[] }>(till(`/admin/units/${encodeURIComponent(go)}`)), enabled: !!go, retry: false });
  return (
    <Card title="Find a unit">
      <div className="flex flex-col gap-3 px-5 pb-5 lg:flex-row lg:items-start">
        <form onSubmit={(e) => { e.preventDefault(); setGo(sn.replace(/[\s-]+/g, "").toUpperCase()); }} className="flex w-full max-w-sm flex-col gap-1.5">
          <Input value={sn} onChange={(e) => setSn(e.target.value)} placeholder="Scan or type an IMEI / serial" className="font-mono" />
          <span className="text-xs text-slate-500">See where that exact device has been.</span>
        </form>
        {q.data ? (
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <b className="text-sm">
              {q.data.item?.name} · {fmtSerial(q.data.unit.serial)} · <Badge tone={q.data.unit.status === "in_stock" ? "brand" : "slate"}>{q.data.unit.status.replace("_", " ")}</Badge>
            </b>
            <div className="flex flex-wrap">
              {q.data.trail.map((m) => (
                <div key={m.id} className="min-w-[150px] flex-1 border-t-4 border-brand pr-3 pt-2">
                  <b className="text-[13px]">{KIND_LABEL[m.kind] ?? m.kind}</b>
                  <span className="block text-xs text-slate-500">
                    {dateTime(m.at)} · {m.ref ?? ""} {m.staffName ? `· ${personName(m.staffName)}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : q.error ? (
          <p className="text-sm text-red-700">{(q.error as Error).message}</p>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Count a whole shop or category in one sitting. The count is blind: the system's number only
 * shows after you type yours, so people count what's there. Saved on this device as you go.
 */
function CountStock({ items, shopId }: { items: Item[]; shopId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const key = `stock-count:${shopId || "all"}`;
  const [counts, setCounts] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? "{}") as Record<string, string>;
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      setCounts(JSON.parse(localStorage.getItem(key) ?? "{}") as Record<string, string>);
    } catch {
      setCounts({});
    }
  }, [key]);
  const [category, setCategory] = useState("");
  const [find, setFind] = useState("");
  const [left, setLeft] = useState(false);
  const [review, setReview] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const countable = useMemo(() => items.filter((i) => i.active && i.tracking === "qty" && !(i.sku ?? "").toUpperCase().startsWith("DELIVERY-")), [items]);
  const save = (next: Record<string, string>) => {
    setCounts(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* still counted on screen */
    }
  };
  const set = (id: string, v: string) => {
    const next = { ...counts };
    if (v === "") delete next[id];
    else next[id] = v;
    save(next);
  };
  const entered = (i: Item) => counts[i.id] !== undefined && counts[i.id] !== "";
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = countable.filter((i) => (!category || i.category === category) && words.every((w) => i.name.toLowerCase().includes(w)) && (!left || !entered(i)));
  const paged = usePaged(shown);
  const done = countable.filter(entered);
  const diffs = done.map((i) => ({ i, diff: Number(counts[i.id]) - (i.onHand ?? 0) })).filter((x) => x.diff !== 0);
  const value = diffs.reduce((n, x) => n + x.diff * (x.i.cost ?? 0), 0);
  const categories = [...new Set(countable.map((i) => i.category).filter(Boolean))].sort();
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api<{ changed: number; same: number }>(till("/admin/stock-count"), { body: { note, lines: done.map((i) => ({ itemId: i.id, counted: Number(counts[i.id]) })) } });
      toast(`Count saved. ${r.changed} updated, ${r.same} already right`);
      save({});
      setReview(false);
      setNote("");
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      void qc.invalidateQueries({ queryKey: ["moves"] });
      void qc.invalidateQueries({ queryKey: ["catalog"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save the count", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      title="Count stock"
      right={<span className="text-[13px] text-slate-500">{done.length} of {countable.length} counted</span>}
    >
      <p className="px-5 pb-3 text-[13px] text-slate-500">Type what's on the shelf. Each product's difference shows after you type its number. Nothing changes until you save, and your count is kept on this device if you leave.</p>
      <div className="mx-5 mb-3 h-2 rounded bg-slate-100"><div className="h-2 rounded bg-brand" style={{ width: `${countable.length ? (done.length / countable.length) * 100 : 0}%` }} /></div>
      <div className="flex flex-wrap items-center gap-2 px-5 pb-3">
        <Input aria-label="Find a product" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a product" className="!h-10 min-w-0 flex-[1_1_200px] sm:max-w-[280px]" />
        <Select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} className="!h-10 !w-auto">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
        <label className="flex min-h-10 items-center gap-2 text-sm"><input type="checkbox" checked={left} onChange={(e) => setLeft(e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" /> Only those not counted</label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr><th className={th}>Product</th><th className={th}>Counted</th><th className={cx(th, "text-right")}>Records say</th><th className={cx(th, "text-right")}>Difference</th></tr></thead>
          <tbody>
            {paged.rows.map((i) => {
              const has = entered(i);
              const diff = has ? Number(counts[i.id]) - (i.onHand ?? 0) : 0;
              return (
                <tr key={i.id}>
                  <td className={cx(td, "whitespace-normal")}><b className="block font-semibold">{i.name}</b><span className="text-xs text-slate-500">{i.category}{i.unit ? ` · by the ${i.unit}` : ""}</span></td>
                  <td className={td}><Input aria-label={`Counted for ${i.name}`} inputMode="numeric" value={counts[i.id] ?? ""} onChange={(e) => set(i.id, e.target.value.replace(/\D/g, ""))} placeholder="0" className="!h-10 !w-28" /></td>
                  <td className={cx(td, "text-right text-slate-500")}>{has ? (i.onHand ?? 0).toLocaleString("en-GB") : "Hidden"}</td>
                  <td className={cx(td, "text-right")}>{!has ? "" : diff === 0 ? <Badge tone="brand">Matches</Badge> : <Badge tone="amber">{diff > 0 ? "+" : ""}{diff.toLocaleString("en-GB")}</Badge>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!shown.length ? <p className="px-5 py-8 text-center text-sm text-slate-500">{left ? "Everything here is counted." : "No products to count."}</p> : null}
      </div>
      <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 px-5 py-3">
        <span className="text-sm text-slate-500">{diffs.length} difference{diffs.length === 1 ? "" : "s"} so far</span>
        {done.length ? <Button onClick={() => save({})}>Clear count</Button> : null}
        <Button tone="primary" disabled={!done.length} onClick={() => setReview(true)}>Review and save</Button>
      </div>
      {review ? (
        <Modal wide title="Save this count?" sub={`${done.length} products counted · ${done.length - diffs.length} match · ${diffs.length} different`} onClose={() => setReview(false)} footer={<><Button onClick={() => setReview(false)}>Keep counting</Button><Button tone="primary" disabled={busy} onClick={() => void submit()}>{busy ? "Saving…" : "Save the count"}</Button></>}>
          <div className="flex flex-col gap-3">
            {diffs.length ? (
              <>
                <div className="flex max-h-72 flex-col overflow-y-auto">
                  {diffs.map(({ i, diff }) => <div key={i.id} className="flex justify-between gap-3 border-b border-slate-100 py-2 text-sm"><span className="min-w-0">{i.name}<span className="block text-xs text-slate-500">Records {(i.onHand ?? 0).toLocaleString("en-GB")}, counted {Number(counts[i.id]).toLocaleString("en-GB")}</span></span><b className="flex-none">{diff > 0 ? "+" : ""}{diff.toLocaleString("en-GB")}</b></div>)}
                </div>
                <p className="text-sm font-semibold">Value of the differences, at cost: {value < 0 ? "− " : ""}{money(Math.abs(value))}</p>
              </>
            ) : <p className="text-sm text-slate-500">Everything counted matches the records.</p>}
            <Field label="Note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={160} placeholder="e.g. Month-end count, Hardware Store" /></Field>
            <p className="text-xs text-slate-500">Saving sets stock to your counted numbers and adds each change to the stock log. Anything sold since you counted a product isn't allowed for, so count when the shop is quiet.</p>
          </div>
        </Modal>
      ) : null}
    </Card>
  );
}

const logDay = (at: number) => new Date(at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

/** Every change to stock, newest first. Rows from one batch (like an import) fold into one line. */
function Log({ shopId }: { shopId: string }) {
  const [kind, setKind] = useState("");
  const [find, setFind] = useState("");
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState<string[]>([]);
  const term = useDeferredValue(find.trim());
  const q = useQuery({
    queryKey: ["moves", kind, shopId, term, page],
    queryFn: () => api<{ moves: Move[]; total: number }>(till(`/admin/moves?kind=${kind}&shop=${encodeURIComponent(shopId)}&q=${encodeURIComponent(term)}&limit=${LOG_PAGE}&offset=${(page - 1) * LOG_PAGE}`)),
    placeholderData: keepPreviousData,
  });
  const moves = q.data?.moves ?? [];
  const serials = moves.some((m) => m.serial);
  const cols = serials ? 7 : 6;
  // Group by day, then fold runs of 4 or more rows from the same batch.
  const days: { day: string; parts: { key: string; rows: Move[] }[] }[] = [];
  for (const m of moves) {
    const day = logDay(m.at);
    let d = days[days.length - 1];
    if (!d || d.day !== day) days.push((d = { day, parts: [] }));
    const key = [m.kind, m.ref, m.note, m.staffName, Math.floor(m.at / 60000)].join("|");
    const last = d.parts[d.parts.length - 1];
    if (last?.key === key) last.rows.push(m);
    else d.parts.push({ key, rows: [m] });
  }
  const line = (m: Move) => (
    <tr key={m.id}>
      <td className={td}>
        <span className="block whitespace-normal font-semibold">{m.itemName}</span>
        <span className="text-xs text-slate-500">{time(m.at)}</span>
      </td>
      <td className={td}><Badge tone={KIND_TONE[m.kind] ?? "slate"}>{KIND_LABEL[m.kind] ?? m.kind}</Badge></td>
      <td className={cx(td, "text-right font-mono font-bold")}>{m.qty > 0 ? `+${m.qty.toLocaleString("en-GB")}` : m.qty.toLocaleString("en-GB")}</td>
      <td className={cx(td, "text-right font-mono")}>{m.balance.toLocaleString("en-GB")}</td>
      {serials ? <td className={cx(td, "font-mono")}>{m.serial ? fmtSerial(m.serial) : "-"}</td> : null}
      <td className={td}>{personName(m.staffName)}</td>
      <td className={cx(td, "text-right")}><span className="font-semibold">{m.ref ?? ""}</span>{m.note ? <span className="block text-xs text-slate-500">{m.note}</span> : null}</td>
    </tr>
  );
  return (
    <Card
      title={PLAIN ? "All changes" : "Stock log"}
      right={
        <div className="flex flex-[1_1_340px] flex-wrap justify-end gap-2">
          <Input aria-label="Search the stock log" value={find} onChange={(e) => { setFind(e.target.value); setPage(1); }} placeholder="Search product" className="!h-10 min-w-0 flex-[1_1_180px] sm:max-w-[280px]" />
          <Select aria-label="Show" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} className="!h-10 !w-auto">
            {[["", "All changes"], ["sales", "Sales"], ["returns", "Returns"], ["received", "Received"], ["transfers", "Transfers"], ["adjust", "Corrections & counts"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["Product", "What", "Change", "In stock", ...(serials ? ["Serial"] : []), "By", "Reference"].map((h) => (
                <th key={h} className={cx(th, (h === "Change" || h === "In stock" || h === "Reference") && "text-right")}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          {days.map((d) => (
            <tbody key={d.day}>
              <tr><td colSpan={cols} className="bg-slate-50 px-3.5 py-2 text-xs font-bold text-slate-600">{d.day}</td></tr>
              {d.parts.map((p) => {
                if (p.rows.length < 4) return <Fragment key={p.key}>{p.rows.map(line)}</Fragment>;
                const open = opened.includes(p.key);
                const m = p.rows[0]!;
                return (
                  <Fragment key={p.key}>
                    <tr>
                      <td colSpan={cols} className="border-b border-slate-50 p-0">
                        <button type="button" aria-expanded={open} onClick={() => setOpened(open ? opened.filter((x) => x !== p.key) : [...opened, p.key])} className="flex min-h-14 w-full items-center justify-between gap-3 !rounded-none px-3.5 py-2 text-left hover:bg-slate-50">
                          <span className="min-w-0">
                            <b className="block text-sm">{m.note || m.ref || KIND_LABEL[m.kind] || m.kind}</b>
                            <span className="text-xs text-slate-500">{p.rows.length} products · {p.rows.reduce((n, x) => n + x.qty, 0).toLocaleString("en-GB")} units · {personName(m.staffName)} · {time(m.at)}</span>
                          </span>
                          <span className="flex-none text-[13px] font-bold text-brand-dark">{open ? "Hide" : "Show all"}</span>
                        </button>
                      </td>
                    </tr>
                    {open ? p.rows.map(line) : null}
                  </Fragment>
                );
              })}
            </tbody>
          ))}
        </table>
        {q.isError ? <p className="px-5 py-8 text-center text-sm text-slate-500">{q.error instanceof Error ? q.error.message : "Couldn't load the log"}</p> : q.data && !moves.length ? <p className="px-5 py-8 text-center text-sm text-slate-500">{find || kind ? "Nothing matches." : "Nothing yet."}</p> : null}
      </div>
      <Pager page={page} size={LOG_PAGE} total={q.data?.total ?? 0} onPage={setPage} />
    </Card>
  );
}
